import { Injectable, computed, effect, inject, signal } from '@angular/core';

import { Subject, of, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { padLeft } from '@shared/functions';
import { BcmSettingsSectionName, DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { applyReservationDefaultTimes } from '@shared/functions/apply-reservation-default-times';
import { DEFAULT_BERTH_RESERVATION_UNIT } from '@modules/bcm/settings/default-units/default-units.component';
import { Season } from '@shared/models/seasons';
import {
    BookingV2BerthOccupancy,
    BookingV2BerthOption,
    BookingV2BoatOption,
    BookingV2BookingDocument,
    BookingV2ElectricMeterAssignment,
    BookingV2KeyAssignment,
    BookingV2PersonOption,
    BookingV2PricingRelationIds,
    BookingV2RelationLineOverrides,
    BookingV2RelationOption,
    BookingV2Service,
    BookingV2Traveler,
    BookingV2WinterStorageOption,
} from '../dtos/booking-v2.dtos';
// Type-only (no circular import — the load mapper also imports facade types).
import type { BookingV2LoadResult } from './booking-v2-load.mapper';
import { serviceFollowsAutoName } from '../utils/booking-v2-service-naming';
import { syncOccupanciesToBookingDateRange } from '../utils/booking-v2-berth-occupancy-dates.util';

export interface BookingV2DateRange {
    from: Date | null;
    to: Date | null;
    openEnd: boolean;
}

// Loaded child row ids, so the commit can express removals as `{ id, deleted }`
// (the backend only deletes children it is told to; travelers are reconciled
// from the full list, so they need no baseline).
export interface BookingV2EditBaseline {
    berthAssignmentIds: number[];
    // Existing berth-less assignment (boat held with no berth), patched not duplicated on edit.
    berthlessAssignmentId: number | null;
    positionIds: number[];
    // Ids of the relation's one-off positions, so edit can remove them before
    // regenerating from the relation (kept out of positionIds — they are not
    // editable services). Paid ones are excluded by the load mapper.
    relationPositionIds: number[];
    // Open positions of the loaded Abos by Abo id — deleted along with a removed Abo (#7044).
    subscriptionPositionIds: Record<number, number[]>;
    // Ids of the key-deposit (Pfand) positions, removed + regenerated from the keys
    // on edit (same pattern as relationPositionIds).
    depositPositionIds: number[];
    subscriptionIds: number[];
    keyAssignmentIds: number[];
    // Only rows shown in the wizard: documents linked later (contract generator)
    // or hidden ones (no file) are never in the diff, so they can't be removed by accident.
    documentIds: number[];
    meters: { assignmentId: number; readingId?: number }[];
    relationAssignmentId: number | null;
}

function emptyEditBaseline(): BookingV2EditBaseline {
    return {
        berthAssignmentIds: [],
        berthlessAssignmentId: null,
        positionIds: [],
        relationPositionIds: [],
        subscriptionPositionIds: {},
        depositPositionIds: [],
        subscriptionIds: [],
        keyAssignmentIds: [],
        documentIds: [],
        meters: [],
        relationAssignmentId: null,
    };
}

const ONBOARDING_STORAGE_KEY = 'yachthafen_onboarding_v1';

export const BOOKING_V2_STEPS_STANDARD: readonly BookingV2Step[] = [
    'date',
    'berth',
    'person',
    'summary',
    'rechnungsversand',
] as const;

export const BOOKING_V2_STEPS_ADVANCED: readonly BookingV2Step[] = [
    'date',
    'berth',
    'person',
    'advanced',
    'summary',
    'rechnungsversand',
] as const;

// Whether to open a Beleg (Rechnung/Quittung) after the booking is saved, and
// of which kind. The selection lives in the Rechnungsversand step; the actual
// prefilled form is opened post-save by the entry layer (the dialog has closed
// by then), populated with the saved booking's open positions.
export interface BookingV2BelegIntent {
    create: boolean;
    kind: 'invoice' | 'receipt';
}

// The cost source a Rechnungsversand row (and ticked Beleg item) comes from.
// Drives source-aware position matching after save (electric matches only
// electric-meter positions, the rest match by product id / title).
export type BookingV2BelegSource = 'relation' | 'service' | 'berth' | 'subscription' | 'electric' | 'deposit';

// A row the user ticked in the Rechnungsversand step. The booking isn't saved
// yet, so it carries the matching info (productId / name / source) used after save
// to pick exactly the corresponding positions for the Beleg — not the whole tab.
export interface BookingV2BelegItem {
    key: string;
    source: BookingV2BelegSource;
    productId: number | null;
    name: string;
}

@Injectable()
export class BookingV2Facade {

    // True while an advanced-tab entry form is open but unsaved. Only the active
    // advanced tab is mounted at a time, so at most one draft can exist.
    readonly advancedDraftDirty = signal<boolean>(false);

    // Pending navigation held back by an unsaved advanced draft: the dialog
    // mounts the u2b confirm while this is set, then calls resolveDraftGuard.
    readonly pendingDraftGuard = signal<(() => void) | null>(null);

    // A step asks the dialog to close and then run an action (e.g. "Beleg anzeigen");
    // the dialog routes it through its unsaved-changes guard.
    private readonly closeRequests = new Subject<() => void>();
    readonly closeRequests$ = this.closeRequests.asObservable();

    // Editing an existing booking (null = create). Drives commit (update vs. add).
    readonly bookingId = signal<number | null>(null);

    readonly isEditMode = computed<boolean>(() => this.bookingId() !== null);

    // Mirrors the legacy BcmBooking.number (padLeft(id, '000')) — the booking
    // number is deterministically derived from the id, so no extra load needed.
    readonly bookingNumber = computed<string>(() => {
        const id = this.bookingId();
        return id !== null ? padLeft(id, '000') : '';
    });

    // Preserved across an edit (no status UI); 1 = "open" default on create.
    readonly bookingStatusId = signal<number>(1);

    readonly baseline = signal<BookingV2EditBaseline>(emptyEditBaseline());

    readonly advancedMode = signal<boolean>(false);

    readonly steps = computed<readonly BookingV2Step[]>(() =>
        this.advancedMode() ? BOOKING_V2_STEPS_ADVANCED : BOOKING_V2_STEPS_STANDARD,
    );

    readonly currentStep = signal<BookingV2Step>('date');

    readonly visitedSteps = signal<ReadonlySet<BookingV2Step>>(new Set(['date']));

    readonly currentStepIndex = computed(() =>
        this.steps().indexOf(this.currentStep()),
    );

    readonly isFirstStep = computed(() => this.currentStepIndex() === 0);

    readonly isLastStep = computed(() => this.currentStepIndex() === this.steps().length - 1);

    readonly bookingType = signal<BookingV2Type>('booking');

    readonly dateRange = signal<BookingV2DateRange>({
        from: null,
        to: null,
        openEnd: false,
    });

    private readonly settingsFacade = inject(BcmSettingsFacade);

    // Marina's configured default check-in/out times (null until settings load).
    readonly defaultTimeUnit = signal<DefaultBerthReservationTimeUnit | null>(null);

    // Stay-length wording follows the tenant's configured unit: overnight marinas
    // count nights ("Übernachtungen"), everything else counts days ("Tage"). Shared
    // by the date-range and summary steps so the label never drifts between them.
    readonly isOvernightUnit = computed(() =>
        this.defaultTimeUnit()?.type === DEFAULT_BERTH_RESERVATION_UNIT.OVERNIGHT_STAY);

    readonly durationUnitLabelKey = computed(() =>
        this.isOvernightUnit() ? 'bookingV2.dateRange.overnights' : 'bookingV2.dateRange.days');

    // True once a blank-open booking has been seeded with default times — the date
    // step uses this to know it may auto-manage times (vs. edit/prefilled ranges,
    // whose times are the user's/entry point's and must never be re-defaulted).
    readonly blankDefaultsApplied = signal<boolean>(false);

    // Set when a blank-open booking asked for default times before settings had
    // loaded; the settings subscribe applies them once available.
    private pendingBlankDefaults = false;

    // Berth occupancies (Liegeplatzbelegungen) — each a berth held for its own
    // time window (6609). Several are allowed (different berths / different
    // periods, or one oversized boat spanning berths). Berth-less occupancies
    // (`berth === null`) are dropped when building the derived berth list below.
    readonly berthOccupancies = signal<readonly BookingV2BerthOccupancy[]>([]);

    // The picked berths, in occupancy order — derived so the electric-meter tab,
    // pricing and the summaries can keep reading a flat berth list unchanged.
    readonly selectedBerths = computed<readonly BookingV2BerthOption[]>(() =>
        this.berthOccupancies()
            .map(o => o.berth)
            .filter((b): b is BookingV2BerthOption => b != null),
    );

    readonly selectedBerthId = computed<number | null>(() => {
        const first = this.selectedBerths()[0];
        return first ? first.id : null;
    });

    // A berthed occupancy with an incomplete/inverted window can't be saved (the
    // backend berth assignment needs a start, and an end unless open). Mirrors the
    // legacy per-assignment validators. Berth-less cards impose no window rule.
    readonly berthOccupanciesInvalid = computed<boolean>(() =>
        this.berthOccupancies().some(o =>
            o.berth != null && (
                !o.from
                || (!o.openEnd && !o.to)
                || (!o.openEnd && !!o.from && !!o.to && o.from > o.to)
            ),
        ),
    );

    readonly selectedBoat = signal<BookingV2BoatOption | null>(null);

    // The booking's main person/organization (`null` until chosen).
    readonly selectedPerson = signal<BookingV2PersonOption | null>(null);

    readonly selectedPersonId = computed<number | null>(() =>
        this.selectedPerson()?.id ?? null,
    );

    // Primary "Beziehung" (tenant relation). Only the primary relation is
    // sent to the backend, which accepts a single tenantRelationAssignment.
    readonly selectedRelation = signal<BookingV2RelationOption | null>(null);

    // Season picked for dynamic-price evaluation of the relation's Leistungen
    // (rule sets can branch on season, e.g. Haupt-/Wintersaison). Not persisted on
    // the booking — purely a pricing input; preselected from the booking dates.
    readonly selectedSeason = signal<Season | null>(null);

    // Winter storage (Lagerplatz) picked for dynamic-price evaluation — rules can
    // branch on it. Picked in the relation card (next to the season), only when a
    // rule needs it. Not a booking field, but the chosen storage round-trips inside
    // the persisted dynamicPrice provenance, so edit recovers it (relation-pricing).
    readonly selectedWinterStorage = signal<BookingV2WinterStorageOption | null>(null);

    readonly selectedWinterStorageId = computed<number | null>(() =>
        this.selectedWinterStorage()?.id ?? null,
    );

    // Beziehung picked per relation Leistung whose price rule names other relations
    // than the booking's own (#7044). It round-trips inside the persisted dynamicPrice
    // provenance of the position, so edit recovers it.
    readonly relationPricingRelationIds = signal<BookingV2PricingRelationIds>({});

    // Hand-edited Bezeichnung / Einzelpreis of the relation Leistungen (#7044). The
    // positions are regenerated on every save, so the edits live here, keyed by product.
    readonly relationLineOverrides = signal<BookingV2RelationLineOverrides>({});

    // Only a fixed-end booking bills the relation's Leistungen as positions. An open-ended
    // one is billed by the subscription scheduler, which knows neither picks nor hand edits —
    // so both only apply (and are only offered) with an end date. (#7044)
    readonly relationLinesBilledAsPositions = computed<boolean>(() => this.dateRange().to != null);
    readonly effectiveRelationLineOverrides = computed<BookingV2RelationLineOverrides>(() =>
        this.relationLinesBilledAsPositions() ? this.relationLineOverrides() : {},
    );
    readonly effectiveRelationPricingRelationIds = computed<BookingV2PricingRelationIds>(() =>
        this.relationLinesBilledAsPositions() ? this.relationPricingRelationIds() : {},
    );

    // "relationId:productId" of the pricing relations already preselected once — kept here,
    // not in the person step, which is recreated on every step change. (#7044)
    readonly relationPricingPreselected = new Set<string>();

    readonly bookingNotes = signal<string>('');

    /** Optional Auftragsbezug (#6739) — `bcm_bookings.bcm_orders_id`. */
    readonly selectedOrderId = signal<number | null>(null);

    // ── Reservation mode (bookingType === 'reservation') ──
    // The confirm-by deadline → persisted as the booking's `reservedUntil`. The
    // Anreise/Abreise range stays the reserved stay period (berth assignment).
    readonly reservationValidUntil = signal<Date | null>(null);

    // Reservation-specific note → persisted as `reservationText` (separate from
    // the general booking note).
    readonly reservationText = signal<string>('');

    // Optional down payment (Anzahlung). When required, the commit emits one
    // ad-hoc "Anzahlung" position that flows through the normal Beleg flow.
    // Create-only convenience — on edit the deposit already lives as a position.
    readonly depositRequired = signal<boolean>(false);
    readonly depositAmount = signal<number | null>(null);
    // Empty → falls back to `reservationValidUntil` (the invoice payment target).
    readonly depositDueDate = signal<Date | null>(null);
    readonly depositTaxRateId = signal<number | null>(null);
    // The selected tax rate's percentage — kept alongside the id so the dispatch
    // step can show the gross deposit without re-fetching the rate list.
    readonly depositVatValue = signal<number>(0);

    // ── Advanced booking step (all optional) ──
    // Additional travelers beyond the main person (who is implicit).
    readonly travelers = signal<readonly BookingV2Traveler[]>([]);

    // Total persons aboard: additional travelers + the booking's main person
    // (the person/company the booking runs on). Drives per-person pricing
    // (Kurtaxe) and the persons-on-board count in the summary.
    readonly personsOnBoard = computed<number>(() =>
        this.travelers().length + (this.selectedPerson() ? 1 : 0),
    );

    readonly electricMeterAssignments = signal<readonly BookingV2ElectricMeterAssignment[]>([]);

    /** After an immediate API delete of a persisted meter assignment (7174). */
    removeElectricMeterFromEditState(assignmentId: number): void {
        this.electricMeterAssignments.update(list => list.filter(a => a.assignmentId !== assignmentId));
        this.baseline.update(b => ({
            ...b,
            meters: b.meters.filter(m => m.assignmentId !== assignmentId),
        }));
    }

    readonly keys = signal<readonly BookingV2KeyAssignment[]>([]);

    readonly services = signal<readonly BookingV2Service[]>([]);

    readonly documents = signal<readonly BookingV2BookingDocument[]>([]);

    // Guards the one-time "default the person to the selected boat's owner"
    // prefill in the person step, so it does not re-fire after the user has
    // removed the prefilled owner. Reset only on a full dialog reset.
    readonly ownerDefaultApplied = signal<boolean>(false);

    // Guards the one-time preselect of the tenant's configured default relation
    // (Settings → default-units `dayGuestBoater`). Create flow only; reset only on
    // a full dialog reset, set in seedFromExisting so edit keeps its own relation.
    readonly relationDefaultApplied = signal<boolean>(false);

    readonly showTour = signal<boolean>(this.shouldShowTourOnInit());

    // Forward navigation is never blocked: the user may move through every step
    // and switch tabs freely. Required fields (date range, committable boat,
    // person, reservation deadline/deposit) are enforced solely at Save, via the
    // commit service's `missingRequirements`. The disabled Save button plus the
    // per-step red badges are the single "still missing / invalid" cue.
    readonly canProceed = computed(() => this.currentStep() !== 'rechnungsversand');

    // ── Unsaved-changes guard (close ✕) ──
    // Serialized signature of every field the user can fill in the wizard,
    // captured once the dialog has finished initializing. The close guard diffs
    // the live signature against it, so an untouched dialog closes without a
    // prompt while any user change triggers the confirmation.
    // null until the baseline is captured — while the edit-mode load is still in
    // flight the dialog holds nothing the user typed, so it counts as clean.
    private readonly pristineSnapshot = signal<string | null>(null);

    // Fields the pricing services rewrite asynchronously (relation object, service
    // prices, season/Lagerplatz) are deliberately reduced to stable, user-driven
    // identifiers or omitted, so a live price re-evaluation never reads as "dirty".
    // Proposed (reparent) meters are auto-suggested, not user input → also omitted.
    private readonly stateSignature = computed<string>(() => {
        const boat = this.selectedBoat();
        const person = this.selectedPerson();
        const date = this.dateRange();
        return JSON.stringify({
        type: this.bookingType(),
        date: {
            from: date.from?.getTime() ?? null,
            to: date.to?.getTime() ?? null,
            openEnd: date.openEnd,
        },
        berths: this.selectedBerths().map(b => b.id),
        boat: boat ? { id: boat.id, name: boat.name, origin: boat.origin ?? 'existing' } : null,
        person: person ? { id: person.id, kind: person.kind } : null,
        // Id only — the relation-pricing service rewrites the object's prices async.
        relation: this.selectedRelation()?.id ?? null,
        relationLineOverrides: Object.entries(this.relationLineOverrides())
            .sort(([a], [b]) => Number(a) - Number(b)),
        // A picked pricing relation changes what the Leistung bills.
        relationPricingRelationIds: Object.entries(this.relationPricingRelationIds())
            .sort(([a], [b]) => Number(a) - Number(b)),
        notes: this.bookingNotes(),
        reservationValidUntil: this.reservationValidUntil()?.getTime() ?? null,
        reservationText: this.reservationText(),
        deposit: {
            required: this.depositRequired(),
            amount: this.depositAmount(),
            dueDate: this.depositDueDate()?.getTime() ?? null,
            taxRateId: this.depositTaxRateId(),
        },
        travelers: this.travelers(),
        keys: this.keys().map(k => k.uuid),
        // Drop the async-priced fields (price / dynamicRuleName / dynamicPrice);
        // keep the user-entered ones so real edits still register.
        services: this.services().map(s => ({
            id: s.id,
            productId: s.productId,
            // A name the rule wrote (and may rewrite) is not user input.
            name: serviceFollowsAutoName(s) ? null : s.name,
            pricingRelationId: s.pricingRelationId ?? null,
            quantity: s.quantity,
            discount: s.discount ?? null,
            serviceStart: s.serviceStart?.getTime() ?? null,
            serviceEnd: s.serviceEnd?.getTime() ?? null,
            isSubscription: s.isSubscription ?? false,
        })),
        documents: this.documents().map(d => d.bcmFilesId),
        meters: this.electricMeterAssignments().filter(m => !m.reparent).map(m => m.uuid),
        });
    });

    // True once the user has filled or changed any wizard field relative to the
    // dialog's initial (opened/loaded) state. Drives the close-✕ confirmation.
    readonly isDialogDirty = computed<boolean>(() => {
        const pristine = this.pristineSnapshot();
        return pristine !== null && this.stateSignature() !== pristine;
    });

    // Records the current wizard state as the clean baseline. Called by the dialog
    // once initialization has settled (after prefill in create, after the load in edit).
    capturePristineState(): void {
        this.pristineSnapshot.set(this.stateSignature());
    }

    // Beleg to open after save (armed in the Rechnungsversand step).
    readonly belegIntent = signal<BookingV2BelegIntent>({ create: false, kind: 'invoice' });

    // Open the contract generator after save (armed via the summary toggle; create-only).
    readonly contractIntent = signal<{ create: boolean }>({ create: false });

    // The rows ticked for the Beleg (only these are transferred, not the whole tab).
    readonly belegSelection = signal<readonly BookingV2BelegItem[]>([]);

    constructor() {
        // Seed the marina's default check-in/out times (cached app-wide; falls
        // back to a load). Applies any blank-open prefill that was queued before
        // settings were available.
        const settings$ = BcmSettingsFacade.lastSettingsState
            ? of(BcmSettingsFacade.lastSettingsState)
            : this.settingsFacade.loadSettings();
        settings$.pipe(take(1)).subscribe(settings => {
            this.defaultTimeUnit.set(
                settings[BcmSettingsSectionName.DefaultBerthReservationTimeUnit] ?? null,
            );
            if (this.pendingBlankDefaults) {
                this.pendingBlankDefaults = false;
                this.applyBlankDefaultTimes();
            }
        });

        // If the user is on `advanced` when advanced mode turns off, the
        // wizard would otherwise sit on a step no longer in the step list.
        effect(() => {
            const step = this.currentStep();
            if (!this.advancedMode() && step === 'advanced') {
                this.goToStep('person');
            }
        });

        // Drop the advanced-only step from the visited set so the stepper does
        // not remember an invisible step.
        effect(() => {
            if (this.advancedMode()) {
                return;
            }
            this.visitedSteps.update(prev => {
                if (!prev.has('advanced')) {
                    return prev;
                }
                const next = new Set(prev);
                next.delete('advanced');
                return next;
            });
        });
    }

    openTour(): void {
        this.showTour.set(true);
    }

    closeTour(): void {
        try {
            localStorage.setItem(ONBOARDING_STORAGE_KEY, 'done');
        } catch {
            // localStorage may be unavailable (private mode, SSR) — fail silently
        }
        this.showTour.set(false);
    }

    setDateRange(partial: Partial<BookingV2DateRange>): void {
        const next = { ...this.dateRange(), ...partial };
        if (next.openEnd) {
            next.to = null;
        }
        this.dateRange.set(next);
        const synced = syncOccupanciesToBookingDateRange(this.berthOccupancies(), next);
        if (synced !== this.berthOccupancies()) {
            this.berthOccupancies.set(synced);
        }
    }

    // Prefills a blank booking's Anreise with today at the default check-in time
    // (open end). Queued if settings have not loaded yet (see constructor).
    requestBlankDefaultTimes(): void {
        if (this.defaultTimeUnit()) {
            this.applyBlankDefaultTimes();
        } else {
            this.pendingBlankDefaults = true;
        }
    }

    private applyBlankDefaultTimes(): void {
        const unit = this.defaultTimeUnit();
        if (!unit) {
            return;
        }
        const { from } = applyReservationDefaultTimes(new Date(), null, unit);
        this.setDateRange({ from, to: null, openEnd: false });
        this.blankDefaultsApplied.set(true);
    }

    setBookingType(type: BookingV2Type): void {
        this.bookingType.set(type);
    }

    // Turn plain preselected berths (e.g. from the map opener) into occupancies,
    // each seeded with the current booking window. One occupancy per berth.
    seedOccupanciesFromBerths(berths: readonly BookingV2BerthOption[]): void {
        const { from, to, openEnd } = this.dateRange();
        this.berthOccupancies.set(
            berths.map(berth => ({ uuid: uuidv4(), berth, from, to, openEnd, overridden: false })),
        );
    }

    // Seeds every step from an existing booking and records the child-id baseline
    // for the commit's removal diff.
    seedFromExisting(result: BookingV2LoadResult): void {
        this.bookingId.set(result.bookingId);
        this.bookingStatusId.set(result.statusId);
        this.bookingType.set(result.bookingType);
        this.dateRange.set(result.dateRange);
        this.berthOccupancies.set([...result.occupancies]);
        this.selectedBoat.set(result.boat);
        this.selectedPerson.set(result.person);
        this.selectedRelation.set(result.relation);
        this.relationPricingRelationIds.set(result.relationPricingRelationIds);
        this.relationLineOverrides.set(result.relationLineOverrides);
        // Lagerplatz recovered from the saved dynamicPrice provenance (not a booking
        // field) so the live re-eval reproduces the stored price (season auto-recovers
        // from the dates instead). The season-style picker stays available to change it.
        this.selectedWinterStorage.set(result.winterStorage);
        this.bookingNotes.set(result.notes);
        this.selectedOrderId.set(result.selectedOrderId);
        this.reservationValidUntil.set(result.reservedUntil);
        this.reservationText.set(result.reservationText);
        // Deposit is create-only; on edit it already exists as a position/service.
        this.depositRequired.set(false);
        this.depositAmount.set(null);
        this.depositDueDate.set(null);
        this.depositTaxRateId.set(null);
        this.depositVatValue.set(0);
        // Post-save contract intent is a create-flow affordance (edit has the
        // dedicated documents-tab button instead).
        this.contractIntent.set({ create: false });
        this.travelers.set([...result.travelers]);
        this.services.set([...result.services]);
        this.electricMeterAssignments.set([...result.electricMeters]);
        this.keys.set([...result.keys]);
        this.documents.set([...result.documents]);
        // Person is already set — suppress the boat-owner default.
        this.ownerDefaultApplied.set(true);
        // Edit keeps the booking's own relation — suppress the default-relation prefill.
        this.relationDefaultApplied.set(true);

        const hasAdvancedData = result.travelers.length > 0
            || result.services.length > 0
            || result.electricMeters.length > 0
            || result.keys.length > 0
            || result.documents.length > 0;
        this.advancedMode.set(hasAdvancedData);

        // Everything is prefilled and all steps are reachable; land on the summary.
        this.visitedSteps.set(new Set(this.steps()));
        this.currentStep.set('summary');

        this.baseline.set({
            berthAssignmentIds: result.occupancies
                .map(o => o.assignmentId)
                .filter((id): id is number => id != null),
            berthlessAssignmentId: result.berthlessAssignmentId,
            positionIds: result.services
                // Protected positions are never sent/deleted → exclude from the diff.
                .filter(s => !s.isSubscription && s.positionId != null && !s.isProtected)
                .map(s => s.positionId as number),
            relationPositionIds: result.relationPositionIds,
            subscriptionPositionIds: result.subscriptionPositionIds,
            depositPositionIds: result.depositPositionIds,
            subscriptionIds: result.services
                .filter(s => s.isSubscription && s.subscriptionId != null)
                .map(s => s.subscriptionId as number),
            keyAssignmentIds: result.keys
                .map(k => k.assignmentId)
                .filter((id): id is number => id != null),
            documentIds: result.documents
                .map(d => d.documentId)
                .filter((id): id is number => id != null),
            meters: result.electricMeters
                .filter(m => m.assignmentId != null)
                .map(m => ({ assignmentId: m.assignmentId as number, readingId: m.readingId })),
            relationAssignmentId: result.relation?.assignmentId ?? null,
        });
    }

    // The picked pricing relations and hand edits belong to the relation's Leistungen;
    // a different (or no) relation starts from its own rules again.
    clearRelationLineInputs(): void {
        this.relationPricingRelationIds.set({});
        this.relationLineOverrides.set({});
        this.relationPricingPreselected.clear();
    }

    toggleAdvancedMode(): void {
        this.advancedMode.update(v => !v);
    }

    // Guards an in-progress advanced-tab entry before navigating away (step
    // change, tab switch, or dialog close): proceeds immediately if clean,
    // otherwise parks the action until the user confirms via resolveDraftGuard.
    guardAdvancedDraft(onProceed: () => void): void {
        if (!this.advancedDraftDirty()) {
            onProceed();
            return;
        }
        this.pendingDraftGuard.set(onProceed);
    }

    requestCloseThen(afterClose: () => void): void {
        this.closeRequests.next(afterClose);
    }

    // Resolves the parked navigation: discard runs it (and clears the draft),
    // cancel just dismisses the prompt.
    resolveDraftGuard(discard: boolean): void {
        const onProceed = this.pendingDraftGuard();
        this.pendingDraftGuard.set(null);
        if (discard) {
            this.advancedDraftDirty.set(false);
            onProceed?.();
        }
    }

    goToStep(step: BookingV2Step): void {
        this.currentStep.set(step);
        this.visitedSteps.update(prev => {
            if (prev.has(step)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(step);
            return next;
        });
    }

    goToNextStep(): void {
        const next = this.steps()[this.currentStepIndex() + 1];
        if (next) {
            this.goToStep(next);
        }
    }

    // Advance to the next wizard step, guarding any in-progress advanced draft.
    // Shared by the footer "Weiter" and the advanced step's in-card pager so both
    // stay in sync.
    advanceToNextStep(): void {
        this.guardAdvancedDraft(() => this.goToNextStep());
    }

    goToPreviousStep(): void {
        const prev = this.steps()[this.currentStepIndex() - 1];
        if (prev) {
            this.goToStep(prev);
        }
    }

    // Jump into the advanced step (edit) from the summary. Advanced data can exist
    // while advancedMode is off (e.g. proposed meters), and the advancedMode effect
    // bounces a stray 'advanced' step back to 'person' — so enable it first.
    goToAdvancedStep(): void {
        this.advancedMode.set(true);
        this.goToStep('advanced');
    }

    isStepReachable(step: BookingV2Step): boolean {
        return this.visitedSteps().has(step);
    }

    reset(): void {
        this.bookingId.set(null);
        this.bookingStatusId.set(1);
        this.baseline.set(emptyEditBaseline());
        this.currentStep.set('date');
        this.visitedSteps.set(new Set(['date']));
        this.bookingType.set('booking');
        this.advancedMode.set(false);
        this.dateRange.set({ from: null, to: null, openEnd: false });
        this.blankDefaultsApplied.set(false);
        this.berthOccupancies.set([]);
        this.selectedBoat.set(null);
        this.selectedPerson.set(null);
        this.selectedRelation.set(null);
        this.clearRelationLineInputs();
        this.selectedSeason.set(null);
        this.selectedWinterStorage.set(null);
        this.bookingNotes.set('');
        this.selectedOrderId.set(null);
        this.reservationValidUntil.set(null);
        this.reservationText.set('');
        this.depositRequired.set(false);
        this.depositAmount.set(null);
        this.depositDueDate.set(null);
        this.depositTaxRateId.set(null);
        this.depositVatValue.set(0);
        this.travelers.set([]);
        this.electricMeterAssignments.set([]);
        this.keys.set([]);
        this.services.set([]);
        this.documents.set([]);
        this.ownerDefaultApplied.set(false);
        this.relationDefaultApplied.set(false);
        this.showTour.set(false);
        this.belegIntent.set({ create: false, kind: 'invoice' });
        this.belegSelection.set([]);
    }

    // ── Reservation completeness (the Save gate in the commit service reads
    // these) ──
    // The confirm-by deadline is required in reservation mode.
    readonly reservationValidUntilMissing = computed<boolean>(() =>
        this.bookingType() === 'reservation' && !this.reservationValidUntil(),
    );

    // A required deposit additionally needs a positive amount and a tax rate.
    readonly depositIncomplete = computed<boolean>(() =>
        this.bookingType() === 'reservation'
        && this.depositRequired()
        && (!((this.depositAmount() ?? 0) > 0) || this.depositTaxRateId() == null),
    );

    private shouldShowTourOnInit(): boolean {
        try {
            return !localStorage.getItem(ONBOARDING_STORAGE_KEY);
        } catch {
            return false;
        }
    }
}

export type BookingV2Step =
    | 'date'
    | 'berth'
    | 'person'
    | 'advanced'
    | 'rechnungsversand'
    | 'summary';

export type BookingV2Type = 'booking' | 'reservation';
